import axios from 'axios';
import { Auth } from 'aws-amplify';

// Create a ref to track if we're already showing the modal to prevent multiple modals
let isShowingSessionExpiredModal = false;
let sessionExpiredCallback = null;

// Background session check interval handle
let sessionCheckInterval = null;
const SESSION_CHECK_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

// Grace period (ms) after login during which we never show session-expired (token may not be in storage yet)
const LOGIN_GRACE_PERIOD_MS = 4000;
let loginGracePeriodEnd = 0;

/** True if we're within the post-login grace period - don't show session expired during this time */
const isWithinLoginGracePeriod = () => Date.now() < loginGracePeriodEnd;

// Public routes where user is NOT "inside" the app (login/signup/landing) - never show session expired here
const PUBLIC_ROUTES = ['/', '/about', '/services', '/contact', '/accessibility-statement', '/oauth/callback', '/login'];

/** True if the request URL is for login/signup/register - 401 there means bad credentials, not session expired */
const isAuthRelatedUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return (
    lower.includes('/login') ||
    lower.includes('/signup') ||
    lower.includes('/register') ||
    lower.includes('/signin') ||
    lower.includes('/oauth') ||
    lower.includes('/public')
  );
};

/** True if user is on a protected app route (inside the website) - only show session expired in that case */
const isUserOnProtectedRoute = () => {
  try {
    const pathname = (typeof window !== 'undefined' && window.location?.pathname) || '';
    return !PUBLIC_ROUTES.includes(pathname);
  } catch {
    return false;
  }
};

const TOKEN_KEYS = ['token', 'bimLiveAuthToken', 'cognito_token', 'idToken'];

/**
 * True if there's any evidence the user HAD a session (auth flag, Cognito LastAuthUser, etc.).
 * Prevents showing "Your session has expired" on initial loads where no session ever existed.
 */
const hadSession = () => {
  try {
    if (typeof sessionStorage !== 'undefined') {
      if (sessionStorage.getItem('auth') === 'true') return true;
      for (const key of TOKEN_KEYS) {
        if (sessionStorage.getItem(key)) return true;
      }
    }
    if (typeof localStorage !== 'undefined') {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('CognitoIdentityServiceProvider.') && key.endsWith('.LastAuthUser')) {
          return true;
        }
      }
    }
  } catch {
    /* storage unavailable */
  }
  return false;
};

/** Returns an object describing token presence in sessionStorage (values are "present" | "missing", never the actual token) */
const getTokenCheckResult = () => {
  const result = {};
  try {
    for (const key of TOKEN_KEYS) {
      const val = sessionStorage.getItem(key);
      result[key] = val != null && String(val).length > 0 ? 'present' : 'missing';
    }
  } catch (e) {
    result.error = String(e?.message || e);
  }
  return result;
};

/** Log why session-expired modal was invoked (call only when we are about to show the modal) */
const logSessionExpiredInvocation = (reason, context) => {
  const pathname = (typeof window !== 'undefined' && window.location?.pathname) || '';
  const tokenCheck = getTokenCheckResult();
  console.group('🔒 Session-expired modal invoked');
  console.log('Why:', reason);
  console.log('What caused it:', context);
  console.log('Current pathname:', pathname);
  console.log('Token lookup (sessionStorage):', tokenCheck);
  console.log('Keys checked (in order):', TOKEN_KEYS.join(', '));
  console.log('auth in sessionStorage:', sessionStorage.getItem('auth'));
  console.log('Within login grace period?:', isWithinLoginGracePeriod());
  console.groupEnd();
};

/**
 * Try to refresh the Cognito session.
 * Returns 'refreshed' if the session was refreshed successfully (access token was stale but refresh token valid).
 * Returns 'expired'   if the refresh token is also expired — user must re-login.
 * Returns 'valid'     if the session was already valid and no refresh was needed.
 */
const checkAndRefreshSession = async () => {
  try {
    const session = await Auth.currentSession();
    // currentSession() auto-refreshes — check if the access token expiry changed
    const expiresIn = session.getAccessToken().getExpiration() * 1000 - Date.now();
    // If expiry is more than 55 min away, the token was just refreshed (was about to expire)
    // If it's <= 60 min, it's either still valid or was just refreshed — either way, good.
    console.log(`🔄 Cognito session valid, access token expires in ${Math.round(expiresIn / 60000)} min`);
    return 'refreshed'; // covers both 'was refreshed' and 'was already valid'
  } catch {
    return 'expired';
  }
};

/**
 * Show the login modal if not already showing and on a protected route.
 */
const triggerSessionExpiredModal = (reason, context) => {
  if (isShowingSessionExpiredModal || !sessionExpiredCallback) return;
  if (!isUserOnProtectedRoute()) return;
  if (isWithinLoginGracePeriod()) return;
  if (!hadSession()) return;
  isShowingSessionExpiredModal = true;
  logSessionExpiredInvocation(reason, context);
  // Close the usage login session with the session-expired reason (best-effort;
  // imported lazily to avoid a circular import with usageSession -> authHeader).
  import('./usageSession')
    .then((m) => m.closeUsageSession('session_expired'))
    .catch(() => {});
  sessionExpiredCallback();
};

/**
 * Public entry point for any module that detects the session is dead
 * (e.g., Amplify Auth calls failing outside of axios). Applies the same guards
 * as the internal interceptor path — safe to call from anywhere.
 */
export const notifySessionExpired = (reason, context) => {
  triggerSessionExpiredModal(reason, context);
};

/**
 * Set the callback function to show the session expired modal
 * @param {Function} callback - Function to call when session expires
 */
export const setSessionExpiredCallback = (callback) => {
  sessionExpiredCallback = callback;
};

/**
 * Reset the session expired modal state and start a short grace period (call after user logs in).
 * During the grace period we do not show the session-expired modal, so token/storage can settle.
 */
export const resetSessionExpiredState = () => {
  isShowingSessionExpiredModal = false;
  loginGracePeriodEnd = Date.now() + LOGIN_GRACE_PERIOD_MS;
};

/**
 * Start a background timer that proactively checks the Cognito session every 15 minutes.
 * If the refresh token has expired the login modal is shown immediately — even if the user
 * is only doing frontend navigation and hasn't triggered an API call.
 * Safe to call multiple times — clears any existing interval first.
 */
export const startSessionCheck = () => {
  if (sessionCheckInterval) clearInterval(sessionCheckInterval);

  sessionCheckInterval = setInterval(async () => {
    if (!isUserOnProtectedRoute() || isWithinLoginGracePeriod()) return;
    const result = await checkAndRefreshSession();
    if (result === 'expired') {
      triggerSessionExpiredModal(
        'Background session check: refresh token has expired',
        { source: 'background interval', intervalMs: SESSION_CHECK_INTERVAL_MS }
      );
    } else {
      // Session still valid — refresh the server-side liveness key so the
      // sweeper doesn't close this session. Fixed-interval, no activity data.
      import('./usageSession').then((m) => m.touchUsageSession()).catch(() => {});
    }
  }, SESSION_CHECK_INTERVAL_MS);

  console.log('✅ Background session check started (every 15 min)');
};

/**
 * Stop the background session check timer (call on logout).
 */
export const stopSessionCheck = () => {
  if (sessionCheckInterval) {
    clearInterval(sessionCheckInterval);
    sessionCheckInterval = null;
  }
};

/**
 * Setup axios interceptors to handle 401 errors and expired tokens.
 * On 401:
 *   - Try Auth.currentSession() (auto-refreshes access token if refresh token still valid)
 *   - If refreshed → retry the original request transparently (user sees nothing)
 *   - If expired  → show the login modal
 */
export const setupAxiosInterceptors = () => {
  // Response interceptor to catch 401 errors
  axios.interceptors.response.use(
    (response) => {
      // If we get a successful response, reset the modal state
      if (response.status === 200 || response.status === 201) {
        isShowingSessionExpiredModal = false;
      }
      return response;
    },
    async (error) => {
      const requestUrl = error.config?.url;

      // Do NOT show session expired for 401 from login/signup/register - that's failed login
      if (isAuthRelatedUrl(requestUrl)) {
        return Promise.reject(error);
      }

      // Only act when user is inside the app (protected route), not on landing/login/signup
      if (!isUserOnProtectedRoute()) {
        return Promise.reject(error);
      }

      // Do not show during post-login grace period (token may still be establishing)
      if (isWithinLoginGracePeriod()) {
        return Promise.reject(error);
      }

      if (error.response && error.response.status === 401) {
        // Prevent retry loops — only retry once
        if (error.config._sessionRetried) {
          triggerSessionExpiredModal(
            'Response interceptor: 401 after token refresh — refresh token expired',
            { source: 'response interceptor (retry)', requestUrl }
          );
          return Promise.reject(error);
        }

        const result = await checkAndRefreshSession();

        if (result === 'refreshed') {
          // Access token was silently refreshed — retry the original request
          console.log('🔄 Token refreshed, retrying request:', requestUrl);
          error.config._sessionRetried = true;
          return axios(error.config);
        }

        // Refresh token expired — user must re-login
        triggerSessionExpiredModal(
          'Response interceptor: 401 and Cognito refresh token is expired',
          {
            source: 'response interceptor',
            requestUrl: requestUrl || error.config?.baseURL,
            status: error.response?.status,
            method: error.config?.method,
          }
        );
      }

      return Promise.reject(error);
    }
  );

  // Request interceptor — no session-expired modal here.
  // We only react on the server's 401 response, not on the outgoing request.
  axios.interceptors.request.use(
    async (config) => config,
    (error) => Promise.reject(error)
  );

  console.log('✅ Axios interceptors setup complete');
};
